import { countryMapping } from 'constants/countries'
import { ExpoPathState, ExpoState, StandData } from 'opensolar-pageeditor'
import { sanitizeHtml } from 'opensolar-pageeditor/src/renderer/utils'
import { CountryMapType } from 'pages/mfa/types'

export const PRODUCT_FILTERS = [
  'Association',
  'Batteries',
  'Battery Optimization',
  'Business Services',
  'Connect & Monitor',
  'Distribution',
  'EV Chargers',
  'Finance',
  'Heat Pump',
  'Imagery',
  'Inverters',
  'Lead Services',
  'Microinverters',
  'Modules',
  'Monitoring',
  'Optimization Software',
  'Other Components',
  'Permitting',
  'Power Storage',
  'Racking & Mounting Systems',
  'Retail Energy',
  'Service Plans',
  'Storage Solutions',
  'Tariffs',
]

export interface StandFilters {
  tags: string | null
  search: string | null
  page?: number
  page_size?: number
  stands_iso2: string | null | undefined
}

export interface PathParts {
  country: string | undefined
  brand: string | undefined
}

const countrySet = new Set(Object.keys(countryMapping))

export const isValidCountryParam = (item: string | undefined) => !!item && countrySet.has(item.toUpperCase())

export const isCountryExpoAvailable = (country: string, countriesWithExpoStands: CountryMapType[]) =>
  countriesWithExpoStands.find((expoCountryMap) => expoCountryMap?.iso2?.toLocaleLowerCase() === country) !== undefined

export const getExpoPathParts = (path: string): PathParts => {
  const pathParts: PathParts = { country: undefined, brand: undefined }

  if (!path.startsWith('/expo')) return pathParts

  const splitPath = path.split('/').map((part) => part.toLowerCase())
  const possibleCountry = splitPath[2]

  if (isValidCountryParam(possibleCountry)) {
    // homepage = ["", "expo", "country"]
    pathParts.country = possibleCountry
  } else {
    // stand = ["", "expo", "brand", "country"]
    pathParts.brand = splitPath[2]
    pathParts.country = splitPath[3]
  }

  if (!isValidCountryParam(pathParts.country)) {
    pathParts.country = undefined
  }

  return pathParts
}

/* Determines what the expo state should be based on the path.

  Valid homepage paths:
  /expo
  /expo/country

  Valid expo stand paths:
  /expo/brand
  /expo/brand/country
  */
export const resolveExpoState = (
  preferredCountry: string | null,
  preferredLocale: string | null,
  availableCountries: CountryMapType[],
  path: string
): ExpoState => {
  const pathParts = getExpoPathParts(path)
  let selectedCountry = pathParts.country ?? preferredCountry ?? 'us'
  let selectedLocale = preferredLocale ?? 'en'
  let isCountryOverrideEnabled = false

  selectedCountry = selectedCountry.toLowerCase()

  if (pathParts.brand && !isCountryExpoAvailable(selectedCountry, availableCountries)) {
    isCountryOverrideEnabled = true
  }

  return {
    selectedCountry,
    selectedLocale,
    brandSlug: pathParts.brand,
    isCountryOverrideEnabled,
  }
}

export const NAV_BAR_PREFERRED_COUNTRY = 'NAV_BAR_PREFERRED_COUNTRY'
export const NAV_BAR_PREFERRED_LOCALE = 'NAV_BAR_PREFERRED_LOCALE'

/** Persist resolved expo country/locale so anonymous visits reuse the last effective selection. */
export const persistExpoPreferencesToLocalStorage = (
  state: Pick<ExpoState, 'selectedCountry' | 'selectedLocale'>
): void => {
  if (state.selectedCountry) {
    localStorage.setItem(NAV_BAR_PREFERRED_COUNTRY, state.selectedCountry)
  }
  if (state.selectedLocale) {
    localStorage.setItem(NAV_BAR_PREFERRED_LOCALE, state.selectedLocale)
  }
}

export const normaliseExpoState = (expoState: Partial<ExpoState>): Partial<ExpoState> => {
  return Object.entries(expoState).reduce((acc, [key, value]) => {
    // Preserve locale case (e.g. en_US) but lowercase other string values
    acc[key] = typeof value === 'string' && key !== 'selectedLocale' ? value.toLowerCase() : value
    return acc
  }, {} as Partial<ExpoState>)
}

export const getExpoPathForState = (expoState: ExpoPathState): string => {
  const normalisedExpoState = normaliseExpoState(expoState)

  if (!normalisedExpoState.brandSlug) {
    return `/expo/${normalisedExpoState.selectedCountry}`
  }
  return `/expo/${normalisedExpoState.brandSlug}/${normalisedExpoState.selectedCountry}`
}

export const asPathState = (expoState: ExpoState): ExpoPathState => {
  return {
    selectedCountry: expoState.selectedCountry,
    selectedLocale: expoState.selectedLocale,
    brandSlug: expoState.brandSlug,
  }
}

export const getOurStoryText = (standData: StandData | null): string => {
  if (!standData) return ''
  const ourStoryElement = standData.expoStand.contents.elements.find((element) => element.contentType === 'our_story')
  if (!ourStoryElement || !ourStoryElement.enabled || ourStoryElement.contentType !== 'our_story') {
    return ''
  }

  const ourStory = ourStoryElement.data?.body || ''
  return sanitizeHtml(ourStory)
}

export const defaultDescription =
  'Discover the OpenSolar Expo - your hub for the latest solar and electrification industry insights and a showcase of our trusted OpenSolar partners across the globe.'
